/**
 * brandExtractor.ts
 *
 * Extracts brand formatting attributes from an uploaded PPTX template file.
 * Ported from brandExtractor.js in deckorate-manifest (1 123 lines → TypeScript).
 *
 * Usage:
 *   const brand = await extractBrand(arrayBuffer);
 */

import { strFromU8, unzip, unzipSync } from "fflate";
import type {
  BrandSpec,
  BrandColors,
  BrandFonts,
  PlaceholderFormat,
  PlaceholderPosition,
  PlaceholderMargins,
  BulletLevel,
  TableFormat,
  TableRowFormat,
} from "../../../types";
import {
  normalizeHex as _norm,
  isNeutralHex,
  isDarkish,
} from "../../../utils/colourUtils";
import { logError, scrubMessage } from "../../../../../telemetry";

// ─── XML namespaces ───────────────────────────────────────────────────────────

const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";
const NS_P = "http://schemas.openxmlformats.org/presentationml/2006/main";
const EMU_PER_PT = 12700;

const APPLY_BRAND_CONNECTOR = "deckorate";

/** Report a non-fatal brandExtractor failure (a best-effort detection step,
 *  or a single malformed XML part) through the shared telemetry pipeline. */
function logBrandExtractError(reason: string, error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  logError(
    "error.connector.failure",
    { type: "BrandExtractError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: APPLY_BRAND_CONNECTOR, reason },
  );
}

// ─── PPTX zip (flat path → bytes map, decompressed via fflate) ───────────────

type PptxZip = Record<string, Uint8Array>;

function unzipPptx(data: ArrayBuffer): Promise<PptxZip> {
  return new Promise((resolve, reject) => {
    unzip(new Uint8Array(data), (err, files) => (err ? reject(err) : resolve(files)));
  });
}

function brandColorByScheme(colors: BrandColors, scheme: string): string | undefined {
  const map: Record<string, string | undefined> = {
    dk1: colors.dk1, dk2: colors.dk2, lt1: colors.lt1, lt2: colors.lt2,
    accent1: colors.accent1, accent2: colors.accent2, accent3: colors.accent3,
    accent4: colors.accent4, accent5: colors.accent5, accent6: colors.accent6,
    hlink: colors.hlink, folHlink: colors.folHlink,
  };
  return map[scheme];
}

function emuToPt(emu: string | null | undefined): number {
  return Math.round((Number.parseInt(emu ?? "0", 10) || 0) / EMU_PER_PT);
}
function hpToPt(hp: string | null | undefined): number {
  return Math.round((Number.parseInt(hp ?? "0", 10) || 0) / 100);
}

function resolveFontName(fontName: string | null | undefined, fonts: BrandFonts): string | null {
  if (!fontName) return null;
  if (fontName.startsWith("+mj")) return fonts.titleFont;
  if (fontName.startsWith("+mn")) return fonts.bodyFont;
  if (fontName.startsWith("+")) return null;
  return fontName;
}

// ─── Active master chain resolution ──────────────────────────────────────────
//
// Walks slide1 → layout → master → theme via .rels files.
// Decks with multiple masters (e.g. 17 themes) reference different theme/master
// pairs per slide. Hardcoding theme1/slideMaster1 reads the wrong brand for any
// deck where the user customised a non-first master.

async function resolveActiveSlideChain(zip: PptxZip): Promise<{
  slide: string;
  layout: string;
  master: string;
  theme: string;
} | null> {
  const slidePaths = Object.keys(zip)
    .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
    .sort((a, b) => {
      const na = Number.parseInt(new RegExp(/slide(\d+)/).exec(a)![1]!, 10);
      const nb = Number.parseInt(new RegExp(/slide(\d+)/).exec(b)![1]!, 10);
      return na - nb;
    });
  if (slidePaths.length === 0) return null;

  const resolveTarget = (
    relsPath: string,
    targetRegex: RegExp,
    abs: (t: string) => string,
  ): Promise<string | null> => {
    const bytes = zip[relsPath];
    if (!bytes) return Promise.resolve(null);
    const xml = strFromU8(bytes);
    const tags = xml.match(/<Relationship\b[^>]*\/>/g) ?? [];
    for (const tag of tags) {
      const m = new RegExp(/\bTarget="([^"]+)"/).exec(tag);
      if (!m?.[1]) continue;
      if (targetRegex.test(m[1])) return Promise.resolve(abs(m[1]));
    }
    return Promise.resolve(null);
  };

  const slidePath = slidePaths[0]!;
  const slideFile = slidePath.split("/").pop()!;

  const layoutAbs = await resolveTarget(
    `ppt/slides/_rels/${slideFile}.rels`,
    /slideLayout\d+\.xml/,
    (t) => `ppt/slideLayouts/${t.split("/").pop()}`,
  );
  if (!layoutAbs) return null;

  const masterAbs = await resolveTarget(
    `ppt/slideLayouts/_rels/${layoutAbs.split("/").pop()}.rels`,
    /slideMaster\d+\.xml/,
    (t) => `ppt/slideMasters/${t.split("/").pop()}`,
  );
  if (!masterAbs) return null;

  const themeAbs = await resolveTarget(
    `ppt/slideMasters/_rels/${masterAbs.split("/").pop()}.rels`,
    /theme\d+\.xml/,
    (t) => `ppt/theme/${t.split("/").pop()}`,
  );
  if (!themeAbs) return null;

  return { slide: slidePath, layout: layoutAbs, master: masterAbs, theme: themeAbs };
}

// ─── Main entry point ─────────────────────────────────────────────────────────

/** Picks which master/theme to read when a file holds several (BRD FR-24, FR-32). */
export interface ExtractBrandOptions {
  readonly themePath?: string;
  readonly masterPath?: string;
}

export async function extractBrand(pptxData: ArrayBuffer, options: ExtractBrandOptions = {}): Promise<BrandSpec> {
  const zip = await unzipPptx(pptxData);

  // Resolve active master chain: slide1 → layout → master → theme.
  // Falls back to theme1/slideMaster1 for single-master decks. An explicit
  // master/theme (the user's pick in a multi-master file) wins.
  const chain = options.themePath && options.masterPath ? null : await resolveActiveSlideChain(zip);
  const themePath = options.themePath ?? chain?.theme ?? "ppt/theme/theme1.xml";
  const masterPath = options.masterPath ?? chain?.master ?? "ppt/slideMasters/slideMaster1.xml";

  const themeXml = await readXml(zip, themePath);
  const colors: BrandColors = themeXml ? extractColors(themeXml) : defaultColors();
  const fonts: BrandFonts = themeXml ? extractFontScheme(themeXml) : defaultFonts();

  const masterXml = await readXml(zip, masterPath);
  const baseTitleFormat = masterXml
    ? extractPlaceholderFormat(masterXml, "title", colors, fonts)
    : defaultTitleFormat(fonts);
  const bodyFormat = masterXml
    ? extractPlaceholderFormat(masterXml, "body", colors, fonts)
    : defaultBodyFormat(fonts);
  const bulletLevels = masterXml
    ? extractBulletLevels(masterXml, colors, fonts)
    : defaultBulletLevels(fonts);

  const allFiles = Object.keys(zip);
  const layoutFiles = allFiles.filter((p) => /^ppt\/slideLayouts\/slideLayout\d+\.xml$/.test(p));
  const slideFiles = allFiles.filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p));

  let titleFormat = await resolveTitleFormat(zip, masterXml, layoutFiles, baseTitleFormat);
  const footnote = await resolveFootnoteFormat(zip, layoutFiles, colors, fonts);
  const subtitle = await resolveSubtitleFormat(zip, layoutFiles, colors, fonts, bodyFormat);
  const footnoteFormat = footnote.format;
  const subtitleFormat = subtitle.format;
  // Styles the source doesn't define (BRD EC-09) — the user can add them in Customise.
  const missingStyles: Array<"title" | "body" | "subtitle" | "footnote"> = [];
  // A style counts as defined when the master OR any layout has its placeholder
  // (decks exported from other tools often define the title only on the layouts).
  if (!(await definesPlaceholder(zip, masterXml, layoutFiles, "title"))) missingStyles.push("title");
  if (!(await definesPlaceholder(zip, masterXml, layoutFiles, "body"))) missingStyles.push("body");
  if (!subtitle.isFound) missingStyles.push("subtitle");
  if (!footnote.isFound) missingStyles.push("footnote");
  const { tableFormat, tableDetected } = await resolveTableFormat(zip, slideFiles, colors);

  const chartFormat = buildChartFormat(colors);
  const fillColorMap = buildFillColorMap(colors);

  const textAnalysis = await analyzeSlideTextColors(zip, slideFiles.slice(0, 15), colors);
  const applied = applyTextColorAnalysis(titleFormat, bodyFormat, colors, textAnalysis);
  titleFormat = applied.titleFormat;
  const highlightColor = applied.highlightColor;

  let bodyArea = null;
  try {
    bodyArea = await detectBodyArea(zip, layoutFiles);
  } catch (error) {
    logBrandExtractError("brand-extract-body-area", error);
  }

  const titleOnlyLayout = await detectTitleOnlyLayout(zip, layoutFiles);

  return {
    colors,
    fonts,
    titleFormat,
    subtitleFormat,
    bodyTextFormat: bodyFormat,
    footnoteFormat,
    bulletLevels,
    tableFormat,
    tableDetected,
    chartFormat,
    fillColorMap,
    lineColor: colors.dk2 ?? colors.accent1 ?? "333333",
    highlightColor,
    bodyArea,
    titleOnlyLayout,
    templateSlideCount: slideFiles.length,
    missingStyles,
  };
}

async function definesPlaceholder(
  zip: PptxZip,
  masterXml: Document | null,
  layoutFiles: string[],
  phType: "title" | "body",
): Promise<boolean> {
  if (masterXml && hasPlaceholder(masterXml, phType)) return true;
  for (const path of layoutFiles) {
    const doc = await readXml(zip, path); // NOSONAR stops at the first layout that defines it
    if (doc && hasPlaceholder(doc, phType)) return true;
  }
  return false;
}

/** True when the document defines a placeholder of this type (so the style is really set). */
function hasPlaceholder(masterDoc: Document, phType: "title" | "body"): boolean {
  for (const ph of masterDoc.getElementsByTagNameNS(NS_P, "ph")) {
    const type = ph.getAttribute("type") ?? "";
    const idx = ph.getAttribute("idx") ?? "";
    if (phType === "title" ? type === "title" || type === "ctrTitle" : type === "body" || idx === "1") return true;
  }
  return false;
}

// ─── extractBrand helpers ─────────────────────────────────────────────────────

async function resolveTitleFormat(
  zip: PptxZip,
  masterXml: Document | null,
  layoutFiles: string[],
  baseTitleFormat: PlaceholderFormat,
): Promise<PlaceholderFormat> {
  const titleSizeCandidates: number[] = [];
  if (masterXml) collectTitleFontSizes(masterXml, titleSizeCandidates);
  for (const path of layoutFiles.slice(0, 12)) {
    const doc = await readXml(zip, path); // NOSONAR results are collected in layout order
    if (doc) collectTitleFontSizes(doc, titleSizeCandidates);
  }
  if (titleSizeCandidates.length === 0) return baseTitleFormat;
  return { ...baseTitleFormat, fontSize: Math.min(...titleSizeCandidates) };
}

async function resolveFootnoteFormat(
  zip: PptxZip,
  layoutFiles: string[],
  colors: BrandColors,
  fonts: BrandFonts,
): Promise<{ format: PlaceholderFormat; isFound: boolean }> {
  for (const path of layoutFiles.slice(0, 5)) {
    const doc = await readXml(zip, path);
    const footnoteFormat = doc ? extractFootnoteFormat(doc, colors, fonts) : null;
    if (footnoteFormat) return { format: footnoteFormat, isFound: true };
  }
  return { format: defaultFootnoteFormat(fonts), isFound: false };
}

async function resolveSubtitleFormat(
  zip: PptxZip,
  layoutFiles: string[],
  colors: BrandColors,
  fonts: BrandFonts,
  bodyFormat: PlaceholderFormat,
): Promise<{ format: PlaceholderFormat; isFound: boolean }> {
  for (const path of layoutFiles) {
    const doc = await readXml(zip, path);
    const subtitleFormat = doc ? extractSubtitleFormat(doc, colors, fonts) : null;
    if (subtitleFormat) return { format: subtitleFormat, isFound: true };
  }
  return {
    isFound: false,
    format: {
      fontName: bodyFormat.fontName ?? fonts.bodyFont,
      fontSize: Math.max((bodyFormat.fontSize ?? 14) + 2, 18),
      bold: false,
      fontColor: colors.dk2 ?? colors.dk1 ?? "444444",
      alignment: "left",
    },
  };
}

async function resolveTableFormat(
  zip: PptxZip,
  slideFiles: string[],
  colors: BrandColors,
): Promise<{ tableFormat: TableFormat; tableDetected: boolean }> {
  for (const path of slideFiles.slice(0, 15)) {
    const doc = await readXml(zip, path);
    const tableFormat = doc ? extractTableFormat(doc, colors) : null;
    if (tableFormat) return { tableFormat, tableDetected: true };
  }
  return { tableFormat: defaultTableFormat(colors), tableDetected: false };
}

function buildChartFormat(colors: BrandColors): { colorSequence: string[] } {
  return {
    colorSequence: [
      colors.accent1, colors.accent2, colors.accent3,
      colors.accent4, colors.accent5, colors.accent6,
    ].filter(Boolean) as string[],
  };
}

function buildFillColorMap(colors: BrandColors): { primary: string; secondary: string; tertiary: string } {
  return {
    primary: colors.accent1 ?? "4472C4",
    secondary: colors.accent2 ?? "ED7D31",
    tertiary: colors.accent3 ?? "A5A5A5",
  };
}

function applyTextColorAnalysis(
  titleFormat: PlaceholderFormat,
  bodyFormat: PlaceholderFormat,
  colors: BrandColors,
  textAnalysis: { bodyColor: string | null; highlightColor: string | null; titleColor: string | null },
): { titleFormat: PlaceholderFormat; highlightColor: string } {
  if (textAnalysis.bodyColor) bodyFormat.fontColor = textAnalysis.bodyColor;

  const resolvedTitleFormat =
    isWeakColor(titleFormat.fontColor, colors) && textAnalysis.titleColor
      ? { ...titleFormat, fontColor: textAnalysis.titleColor }
      : titleFormat;

  const highlightColor =
    textAnalysis.highlightColor && textAnalysis.highlightColor !== textAnalysis.bodyColor
      ? textAnalysis.highlightColor
      : (_norm(colors.accent1) ?? _norm(colors.dk1) ?? "4472C4");

  return { titleFormat: resolvedTitleFormat, highlightColor };
}

async function detectTitleOnlyLayout(
  zip: PptxZip,
  layoutFiles: string[],
): Promise<{ path: string; name: string; type: string; xml: string } | null> {
  try {
    for (const path of layoutFiles) {
      const found = await findTitleOnlyLayout(zip, path);
      if (found) return found;
    }
  } catch (error) {
    logBrandExtractError("brand-extract-title-only-layout", error);
  }
  return null;
}

async function findTitleOnlyLayout(
  zip: PptxZip,
  path: string,
): Promise<{ path: string; name: string; type: string; xml: string } | null> {
  const xmlStr = await readRawString(zip, path);
  if (!xmlStr) return null;
  const doc = new DOMParser().parseFromString(xmlStr, "text/xml");
  const sldLayout = doc.getElementsByTagNameNS(NS_P, "sldLayout")[0];
  if (!sldLayout) return null;
  const layoutType = sldLayout.getAttribute("type") ?? "";
  const cSld = sldLayout.getElementsByTagNameNS(NS_P, "cSld")[0];
  const layoutName = cSld?.getAttribute("name") ?? "";
  if (layoutType !== "titleOnly" && !isTitleOnlyName(layoutName)) return null;
  return { path, name: layoutName, type: layoutType, xml: xmlStr };
}

// ─── Color extraction ─────────────────────────────────────────────────────────

function extractColors(doc: Document): BrandColors {
  const scheme = doc.getElementsByTagNameNS(NS_A, "clrScheme")[0];
  if (!scheme) return defaultColors();

  const slots = [
    "dk1", "dk2", "lt1", "lt2",
    "accent1", "accent2", "accent3", "accent4", "accent5", "accent6",
    "hlink", "folHlink",
  ] as const;

  const colors: Partial<BrandColors> = {};
  for (const slot of slots) {
    const el = scheme.getElementsByTagNameNS(NS_A, slot)[0];
    if (!el) continue;
    const srgb = el.getElementsByTagNameNS(NS_A, "srgbClr")[0];
    if (srgb) { colors[slot] = srgb.getAttribute("val") ?? ""; continue; }
    const sys = el.getElementsByTagNameNS(NS_A, "sysClr")[0];
    if (sys) colors[slot] = sys.getAttribute("lastClr") ?? sys.getAttribute("val") ?? "";
  }
  return { ...defaultColors(), ...colors };
}

// ─── Font scheme extraction ───────────────────────────────────────────────────

function extractFontScheme(doc: Document): BrandFonts {
  const fonts: BrandFonts = { titleFont: "Calibri Light", bodyFont: "Calibri", footnoteFont: "Calibri" };
  const fontScheme = doc.getElementsByTagNameNS(NS_A, "fontScheme")[0];
  if (!fontScheme) return fonts;
  const major = fontScheme.getElementsByTagNameNS(NS_A, "majorFont")[0];
  if (major) {
    const latin = major.getElementsByTagNameNS(NS_A, "latin")[0];
    if (latin) fonts.titleFont = latin.getAttribute("typeface") ?? fonts.titleFont;
  }
  const minor = fontScheme.getElementsByTagNameNS(NS_A, "minorFont")[0];
  if (minor) {
    const latin = minor.getElementsByTagNameNS(NS_A, "latin")[0];
    if (latin) { fonts.bodyFont = latin.getAttribute("typeface") ?? fonts.bodyFont; fonts.footnoteFont = fonts.bodyFont; }
  }
  return fonts;
}

// ─── Collect title font sizes ─────────────────────────────────────────────────

function collectTitleFontSizes(doc: Document, candidates: number[]): void {
  const shapes = doc.getElementsByTagNameNS(NS_P, "sp");
  for (const element of shapes) {
    const ph = element!.getElementsByTagNameNS(NS_P, "ph")[0];
    if (ph?.getAttribute("type") !== "title") continue;
    const allDefRPr = element!.getElementsByTagNameNS(NS_A, "defRPr");
    for (const element of allDefRPr) {
      const sz = element!.getAttribute("sz");
      if (sz) { candidates.push(hpToPt(sz)); break; }
    }
  }
}

// ─── Placeholder format extraction ───────────────────────────────────────────

interface RunOverrides {
  fontSize?: number;
  bold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  fontColor?: string;
  fontName?: string;
}

// Reads sz/b/i/u/latin/solidFill overrides from the first candidate that
// defines each attribute (fontSize scans all candidates; the rest use candidates[0]).
function resolveRunOverrides(candidates: Element[], colors: BrandColors, fonts: BrandFonts): RunOverrides {
  let fontSize: number | undefined;
  for (const dr of candidates) {
    const sz = dr.getAttribute("sz");
    if (sz) { fontSize = hpToPt(sz); break; }
  }

  const defRPr = candidates[0];
  if (!defRPr) return { fontSize };

  let bold: boolean | undefined;
  if (defRPr.getAttribute("b") === "1") bold = true;
  if (defRPr.getAttribute("b") === "0") bold = false;

  let isItalic: boolean | undefined;
  if (defRPr.getAttribute("i") === "1") isItalic = true;
  if (defRPr.getAttribute("i") === "0") isItalic = false;

  // OOXML underline (<a:rPr u="...">) is a style enum ("sng", "dbl", ...), not
  // a boolean — "none" (or absent) means off, any other value means on.
  let isUnderline: boolean | undefined;
  const u = defRPr.getAttribute("u");
  if (u !== null) isUnderline = u !== "none";

  const latin = defRPr.getElementsByTagNameNS(NS_A, "latin")[0];
  const fontName = latin ? (resolveFontName(latin.getAttribute("typeface"), fonts) ?? undefined) : undefined;
  const fontColor = resolveRunColor(defRPr, colors) ?? undefined;

  return { fontSize, bold, isItalic, isUnderline, fontColor, fontName };
}

function extractPlaceholderFormat(
  masterDoc: Document,
  phType: "title" | "body",
  colors: BrandColors,
  fonts: BrandFonts,
): PlaceholderFormat {
  const shapes = masterDoc.getElementsByTagNameNS(NS_P, "sp");
  for (const sp of shapes) {
    const format = tryExtractPlaceholderFromShape(sp!, phType, colors, fonts);
    if (format) return format;
  }

  if (phType === "title") {
    const fallback = extractCtrTitleFallback(masterDoc, colors, fonts);
    if (fallback) return fallback;
  }

  return phType === "title" ? defaultTitleFormat(fonts) : defaultBodyFormat(fonts);
}

function tryExtractPlaceholderFromShape(
  sp: Element,
  phType: "title" | "body",
  colors: BrandColors,
  fonts: BrandFonts,
): PlaceholderFormat | null {
  const phEls = sp.getElementsByTagNameNS(NS_P, "ph");
  if (phEls.length === 0) return null;
  const ph = phEls[0]!;
  const type = ph.getAttribute("type") ?? "";
  const idx = ph.getAttribute("idx") ?? "";
  const isMatch =
    (phType === "title" && type === "title") ||
    (phType === "body" && (type === "body" || idx === "1"));
  if (!isMatch) return null;

  const overrides = resolveRunOverrides(collectDefRPrCandidates(sp), colors, fonts);

  return {
    fontName: overrides.fontName ?? (phType === "title" ? fonts.titleFont : fonts.bodyFont),
    fontSize: overrides.fontSize ?? (phType === "title" ? 28 : 14),
    bold: overrides.bold ?? (phType === "title"),
    isItalic: overrides.isItalic,
    isUnderline: overrides.isUnderline,
    fontColor: overrides.fontColor ?? (colors.dk1 ?? "000000"),
    alignment: "left",
    position: extractPosition(sp),
    margins: extractMargins(sp),
  };
}

function collectDefRPrCandidates(sp: Element): Element[] {
  const candidates: Element[] = [];
  const lstStyle = sp.getElementsByTagNameNS(NS_A, "lstStyle")[0];
  const lvl1 = lstStyle?.getElementsByTagNameNS(NS_A, "lvl1pPr")[0];
  const lvl1DefRPr = lvl1?.getElementsByTagNameNS(NS_A, "defRPr")[0];
  if (lvl1DefRPr) candidates.push(lvl1DefRPr);
  candidates.push(...sp.getElementsByTagNameNS(NS_A, "defRPr"));
  return candidates;
}

function extractCtrTitleFallback(masterDoc: Document, colors: BrandColors, fonts: BrandFonts): PlaceholderFormat | null {
  const shapes = masterDoc.getElementsByTagNameNS(NS_P, "sp");
  for (const element of shapes) {
    const sp = element!;
    const phEls = sp.getElementsByTagNameNS(NS_P, "ph");
    if (phEls.length === 0 || phEls[0]!.getAttribute("type") !== "ctrTitle") continue;
    const defRPr = sp.getElementsByTagNameNS(NS_A, "defRPr")[0];
    const sz = defRPr?.getAttribute("sz");
    if (!sz) continue;
    return {
      fontName: fonts.titleFont,
      fontSize: hpToPt(sz),
      bold: true,
      fontColor: colors.dk1 ?? "000000",
      alignment: "center",
      position: extractPosition(sp),
      margins: extractMargins(sp),
    };
  }
  return null;
}

// ─── Bullet levels ────────────────────────────────────────────────────────────

function extractBulletLevels(masterDoc: Document, colors: BrandColors, fonts: BrandFonts): BulletLevel[] {
  const bodyShape = findBulletBodyShape(masterDoc);
  if (!bodyShape) return defaultBulletLevels(fonts);

  const levels: BulletLevel[] = [];
  for (let lvl = 1; lvl <= 5; lvl++) {
    levels.push(extractBulletLevelAt(bodyShape, lvl, colors, fonts));
  }
  return levels;
}

function findBulletBodyShape(masterDoc: Document): Element | null {
  const shapes = masterDoc.getElementsByTagNameNS(NS_P, "sp");
  for (const element of shapes) {
    const phEls = element!.getElementsByTagNameNS(NS_P, "ph");
    if (phEls.length === 0) continue;
    const t = phEls[0]!.getAttribute("type") ?? "";
    const idx = phEls[0]!.getAttribute("idx") ?? "";
    if (t === "body" || idx === "1") return element!;
  }
  return null;
}

function emptyBulletLevel(lvl: number, colors: BrandColors, fonts: BrandFonts): BulletLevel {
  return {
    level: lvl - 1,
    fontSize: Math.max(10, 14 - (lvl - 1) * 2),
    fontName: fonts.bodyFont,
    fontColor: colors.dk1 ?? "333333",
    indentEmu: 228600 * lvl,
    hangingEmu: -228600,
    bulletChar: "•",
  };
}

function extractBulletLevelAt(bodyShape: Element, lvl: number, colors: BrandColors, fonts: BrandFonts): BulletLevel {
  const pPr = bodyShape.getElementsByTagNameNS(NS_A, `lvl${lvl}pPr`)[0];
  if (!pPr) return emptyBulletLevel(lvl, colors, fonts);

  const marL = Number.parseInt(pPr.getAttribute("marL") ?? "0", 10);
  const indent = Number.parseInt(pPr.getAttribute("indent") ?? "0", 10);

  const buChar = pPr.getElementsByTagNameNS(NS_A, "buChar")[0];
  const bulletChar = buChar?.getAttribute("char") ?? "•";
  const buFont = pPr.getElementsByTagNameNS(NS_A, "buFont")[0];
  const bulletFont = buFont?.getAttribute("typeface") ?? null;

  const defRPr = pPr.getElementsByTagNameNS(NS_A, "defRPr")[0];
  const overrides = resolveRunOverrides(defRPr ? [defRPr] : [], colors, fonts);

  return {
    level: lvl - 1,
    fontSize: overrides.fontSize ?? Math.max(10, 14 - (lvl - 1) * 2),
    fontName: overrides.fontName ?? fonts.bodyFont,
    fontColor: overrides.fontColor ?? (colors.dk1 ?? "333333"),
    indentEmu: marL,
    hangingEmu: indent,
    bulletChar,
    bulletFont,
  };
}

// ─── Subtitle format ──────────────────────────────────────────────────────────

function extractSubtitleFormat(layoutDoc: Document, colors: BrandColors, fonts: BrandFonts): PlaceholderFormat | null {
  const sps = layoutDoc.getElementsByTagNameNS(NS_P, "sp");
  for (const element of sps) {
    const format = trySubtitleFromShape(element!, colors, fonts);
    if (format) return format;
  }
  return null;
}

function trySubtitleFromShape(sp: Element, colors: BrandColors, fonts: BrandFonts): PlaceholderFormat | null {
  const ph = sp.getElementsByTagNameNS(NS_P, "ph")[0];
  if (!ph) return null;
  const type = ph.getAttribute("type") ?? "";
  const idx = ph.getAttribute("idx") ?? "";
  if (!(type === "subTitle" || (type === "body" && idx === "1"))) return null;

  const defRPr = sp.getElementsByTagNameNS(NS_A, "defRPr")[0];
  const overrides = resolveRunOverrides(defRPr ? [defRPr] : [], colors, fonts);

  return {
    fontName: overrides.fontName ?? fonts.bodyFont,
    fontSize: overrides.fontSize ?? 18,
    bold: overrides.bold ?? false,
    isItalic: overrides.isItalic,
    isUnderline: overrides.isUnderline,
    fontColor: overrides.fontColor ?? (colors.dk2 ?? colors.dk1 ?? "444444"),
    alignment: "left",
  };
}

// ─── Footnote format ──────────────────────────────────────────────────────────

const FOOTNOTE_PLACEHOLDER_TYPES = new Set(["ftr", "dt", "sldNum"]);

function extractFootnoteFormat(layoutDoc: Document, colors: BrandColors, fonts: BrandFonts): PlaceholderFormat | null {
  const shapes = layoutDoc.getElementsByTagNameNS(NS_P, "sp");
  for (const element of shapes) {
    const format = tryFootnoteFromShape(element!, colors, fonts);
    if (format) return format;
  }
  return null;
}

function tryFootnoteFromShape(sp: Element, colors: BrandColors, fonts: BrandFonts): PlaceholderFormat | null {
  const phEls = sp.getElementsByTagNameNS(NS_P, "ph");
  if (phEls.length === 0) return null;
  const type = phEls[0]!.getAttribute("type") ?? "";
  if (!FOOTNOTE_PLACEHOLDER_TYPES.has(type)) return null;

  const defRPr = sp.getElementsByTagNameNS(NS_A, "defRPr")[0];
  const overrides = resolveRunOverrides(defRPr ? [defRPr] : [], colors, fonts);

  return {
    fontName: overrides.fontName ?? fonts.footnoteFont,
    fontSize: overrides.fontSize ?? 8,
    bold: overrides.bold ?? false,
    isItalic: overrides.isItalic,
    isUnderline: overrides.isUnderline,
    fontColor: overrides.fontColor ?? (colors.dk2 ?? "888888"),
    alignment: "left",
    position: extractPosition(sp),
  };
}

// ─── Table format ─────────────────────────────────────────────────────────────

function extractTableFormat(slideDoc: Document, colors: BrandColors): TableFormat | null {
  const tables = slideDoc.getElementsByTagNameNS(NS_A, "tbl");
  if (tables.length === 0) return null;
  const tbl = tables[0]!;
  const rows = tbl.getElementsByTagNameNS(NS_A, "tr");
  if (rows.length === 0) return null;
  const headerRow = extractRowFormat(rows[0]!, colors);
  const bodyRow = rows.length > 1
    ? extractRowFormat(rows[1]!, colors)
    : { fillColor: "FFFFFF", fontColor: "333333", fontName: "Calibri", fontSize: 10, bold: false };
  let borderColor = "D2D0CE";
  const tblPr = tbl.getElementsByTagNameNS(NS_A, "tblPr")[0];
  if (tblPr) {
    const lnEls = tblPr.getElementsByTagNameNS(NS_A, "ln");
    for (const element of lnEls) {
      const srgb = element!.getElementsByTagNameNS(NS_A, "srgbClr")[0];
      if (srgb) { borderColor = srgb.getAttribute("val") ?? borderColor; break; }
    }
  }
  return { headerRow, bodyRow, borderColor, borderWidth: 1 };
}

function defaultCellFormat(): TableRowFormat {
  return { fillColor: "FFFFFF", fontColor: "333333", fontName: "Calibri", fontSize: 10, bold: false };
}

function extractRowFormat(rowEl: Element, colors: BrandColors): TableRowFormat {
  const cells = rowEl.getElementsByTagNameNS(NS_A, "tc");
  if (cells.length === 0) return defaultCellFormat();
  const cell = cells[0]!;
  return {
    fillColor: extractCellFillColor(cell, colors),
    ...extractCellTextFormat(cell, colors),
  };
}

function extractCellFillColor(cell: Element, colors: BrandColors): string {
  const tcPr = cell.getElementsByTagNameNS(NS_A, "tcPr")[0];
  if (!tcPr) return "FFFFFF";
  const srgb = tcPr.getElementsByTagNameNS(NS_A, "srgbClr")[0];
  if (srgb) return srgb.getAttribute("val") ?? "FFFFFF";
  const schemeClr = tcPr.getElementsByTagNameNS(NS_A, "schemeClr")[0];
  if (!schemeClr) return "FFFFFF";
  const v = schemeClr.getAttribute("val");
  return (v && brandColorByScheme(colors, v)) || "FFFFFF";
}

function extractCellTextFormat(cell: Element, colors: BrandColors): Omit<TableRowFormat, "fillColor"> {
  const rPr = cell.getElementsByTagNameNS(NS_A, "rPr")[0];
  if (!rPr) return { fontColor: "333333", fontName: "Calibri", fontSize: 10, bold: false };

  const sz = rPr.getAttribute("sz");
  const fontSize = sz ? hpToPt(sz) : 10;
  const bold = rPr.getAttribute("b") === "1";
  const latin = rPr.getElementsByTagNameNS(NS_A, "latin")[0];
  const tc = latin?.getAttribute("typeface") ?? "";
  const fontName = !tc.startsWith("+") && tc.length > 0 ? tc : "Calibri";
  const fontColor = resolveRunColor(rPr, colors) ?? "333333";

  return { fontColor, fontName, fontSize, bold };
}

// ─── Body-area detection ──────────────────────────────────────────────────────

async function detectBodyArea(zip: PptxZip, layoutFiles: string[]) {
  const { slideW, slideH } = await getSlideDimensions(zip);
  const { biggestBody, biggestBodyLayoutTitle } = await findBiggestBodyArea(zip, layoutFiles);
  if (!biggestBody) return null;

  const titleOnSide = computeTitleOnSide(biggestBody, biggestBodyLayoutTitle, slideW);

  return {
    left: Math.round(biggestBody.left),
    top: Math.round(biggestBody.top),
    width: Math.round(biggestBody.width),
    height: Math.round(biggestBody.height),
    titleOnSide,
    slideW: Math.round(slideW),
    slideH: Math.round(slideH),
  };
}

async function getSlideDimensions(zip: PptxZip): Promise<{ slideW: number; slideH: number }> {
  const presXml = await readXml(zip, "ppt/presentation.xml");
  const sldSz = presXml?.getElementsByTagNameNS(NS_P, "sldSz")[0];
  if (!sldSz) return { slideW: 960, slideH: 540 };

  const cx = Number.parseInt(sldSz.getAttribute("cx") ?? "0", 10);
  const cy = Number.parseInt(sldSz.getAttribute("cy") ?? "0", 10);
  return {
    slideW: cx > 0 ? cx / EMU_PER_PT : 960,
    slideH: cy > 0 ? cy / EMU_PER_PT : 540,
  };
}

async function findBiggestBodyArea(
  zip: PptxZip,
  layoutFiles: string[],
): Promise<{ biggestBody: PlaceholderPosition | null; biggestBodyLayoutTitle: PlaceholderPosition | null }> {
  let biggestBody: PlaceholderPosition | null = null;
  let biggestBodyLayoutTitle: PlaceholderPosition | null = null;

  for (const path of layoutFiles) {
    const doc = await readXml(zip, path); // NOSONAR results are collected in layout order
    if (!doc) continue;
    const { layoutTitle, layoutBody } = findLayoutTitleAndBody(doc);
    const isBigger = layoutBody && (!biggestBody || layoutBody.width * layoutBody.height > biggestBody.width * biggestBody.height);
    if (isBigger) {
      biggestBody = layoutBody;
      biggestBodyLayoutTitle = layoutTitle;
    }
  }
  return { biggestBody, biggestBodyLayoutTitle };
}

function findLayoutTitleAndBody(doc: Document): {
  layoutTitle: PlaceholderPosition | null;
  layoutBody: PlaceholderPosition | null;
} {
  const sps = doc.getElementsByTagNameNS(NS_P, "sp");
  let layoutTitle: PlaceholderPosition | null = null;
  let layoutBody: PlaceholderPosition | null = null;

  for (const element of sps) {
    const sp = element!;
    const ph = sp.getElementsByTagNameNS(NS_P, "ph")[0];
    if (!ph) continue;
    const type = ph.getAttribute("type") ?? "";
    const pos = extractPosition(sp);
    if (!pos?.width || !pos.height) continue;

    if (type === "title") {
      if (!layoutTitle || pos.width * pos.height > layoutTitle.width * layoutTitle.height) layoutTitle = pos;
    } else if (type === "body" || type === "") {
      if (!layoutBody || pos.width * pos.height > layoutBody.width * layoutBody.height) layoutBody = pos;
    }
  }
  return { layoutTitle, layoutBody };
}

function computeTitleOnSide(
  biggestBody: PlaceholderPosition,
  layoutTitle: PlaceholderPosition | null,
  slideW: number,
): "left" | "right" | null {
  if (!layoutTitle) return null;
  const isShortWide = layoutTitle.width > layoutTitle.height * 2;
  if (isShortWide) return null;

  const tCx = layoutTitle.left + layoutTitle.width / 2;
  if (tCx < slideW * 0.4 && biggestBody.left > slideW * 0.25) return "left";
  if (tCx > slideW * 0.6 && biggestBody.left + biggestBody.width < slideW * 0.75) return "right";
  return null;
}

// ─── Slide text color analysis ────────────────────────────────────────────────

type RoleColorCounts = Record<"title" | "body" | "other", Map<string, number>>;

async function analyzeSlideTextColors(
  zip: PptxZip,
  slideFiles: string[],
  colors: BrandColors,
): Promise<{ bodyColor: string | null; highlightColor: string | null; titleColor: string | null; totalSamples: number }> {
  const { byRole, totalSamples } = await collectRoleColorCounts(zip, slideFiles, colors);

  const combined = combineColorCounts(byRole.body, byRole.other);
  const bodyColor = pickBodyColor(combined);
  const highlightColor = pickHighlightColor(combined, bodyColor);
  const titleColor = pickTopColor(byRole.title);

  return { bodyColor, highlightColor, titleColor, totalSamples };
}

async function collectRoleColorCounts(
  zip: PptxZip,
  slideFiles: string[],
  colors: BrandColors,
): Promise<{ byRole: RoleColorCounts; totalSamples: number }> {
  const byRole: RoleColorCounts = {
    title: new Map<string, number>(),
    body: new Map<string, number>(),
    other: new Map<string, number>(),
  };
  let totalSamples = 0;

  for (const slidePath of slideFiles) {
    const doc = await readXml(zip, slidePath); // NOSONAR results are collected in slide order
    if (!doc) continue;
    const sps = doc.getElementsByTagNameNS(NS_P, "sp");
    for (const element of sps) {
      totalSamples += tallyShapeColors(element!, colors, byRole);
    }
  }
  return { byRole, totalSamples };
}

function tallyShapeColors(sp: Element, colors: BrandColors, byRole: RoleColorCounts): number {
  const role = classifyShapeRole(sp);
  const nodes = collectShapeColorNodes(sp);
  let sampleCount = 0;
  for (const n of nodes) {
    const hex = resolveRunColor(n, colors);
    if (!hex) continue;
    const H = hex.toUpperCase();
    const m = byRole[role];
    m.set(H, (m.get(H) ?? 0) + 1);
    sampleCount++;
  }
  return sampleCount;
}

function classifyShapeRole(sp: Element): "title" | "body" | "other" {
  const ph = sp.getElementsByTagNameNS(NS_P, "ph")[0];
  if (!ph) return "other";
  const phType = ph.getAttribute("type") ?? "";
  if (phType.includes("title")) return "title";
  if (phType === "body" || ph.getAttribute("idx") === "1") return "body";
  return "other";
}

function collectShapeColorNodes(sp: Element): Element[] {
  const rPrs = sp.getElementsByTagNameNS(NS_A, "rPr");
  const defRPrs = sp.getElementsByTagNameNS(NS_A, "defRPr");
  return [...rPrs, ...defRPrs];
}

function combineColorCounts(...maps: Map<string, number>[]): Map<string, number> {
  const combined = new Map<string, number>();
  for (const m of maps) {
    for (const [h, n] of m) combined.set(h, (combined.get(h) ?? 0) + n);
  }
  return combined;
}

function pickBodyColor(combined: Map<string, number>): string | null {
  const sorted = [...combined.entries()].sort((a, b) => b[1] - a[1]);
  for (const [h] of sorted) {
    if (isDarkish(h)) return h;
  }
  return sorted.length > 0 ? sorted[0]![0] : null;
}

function pickHighlightColor(combined: Map<string, number>, bodyColor: string | null): string | null {
  const bodyCount = bodyColor ? (combined.get(bodyColor) ?? 1) : 1;
  const sortedAccent = [...combined.entries()]
    .filter(([h]) => !isNeutralHex(h) && h !== bodyColor)
    .sort((a, b) => b[1] - a[1]);
  for (const [h, n] of sortedAccent) {
    if (n <= bodyCount * 0.5) return h;
  }
  return sortedAccent.length > 0 ? sortedAccent[0]![0] : null;
}

function pickTopColor(map: Map<string, number>): string | null {
  const sorted = [...map.entries()].sort((a, b) => b[1] - a[1]);
  return sorted.length > 0 ? (sorted[0]![0] ?? null) : null;
}

// ─── Shared XML helpers ───────────────────────────────────────────────────────

function extractPosition(shapeEl: Element): PlaceholderPosition | null {
  const xfrm = shapeEl.getElementsByTagNameNS(NS_A, "xfrm")[0] ?? shapeEl.getElementsByTagNameNS(NS_P, "xfrm")[0];
  if (!xfrm) return null;
  const off = xfrm.getElementsByTagNameNS(NS_A, "off")[0];
  const ext = xfrm.getElementsByTagNameNS(NS_A, "ext")[0];
  if (!off || !ext) return null;
  return {
    left: emuToPt(off.getAttribute("x")),
    top: emuToPt(off.getAttribute("y")),
    width: emuToPt(ext.getAttribute("cx")),
    height: emuToPt(ext.getAttribute("cy")),
  };
}

function extractMargins(shapeEl: Element): PlaceholderMargins {
  const bodyPr = shapeEl.getElementsByTagNameNS(NS_A, "bodyPr")[0];
  if (!bodyPr) return { left: 7, right: 7, top: 4, bottom: 4 };
  return {
    left: emuToPt(bodyPr.getAttribute("lIns") ?? "91440"),
    right: emuToPt(bodyPr.getAttribute("rIns") ?? "91440"),
    top: emuToPt(bodyPr.getAttribute("tIns") ?? "45720"),
    bottom: emuToPt(bodyPr.getAttribute("bIns") ?? "45720"),
  };
}

function resolveRunColor(rPrEl: Element, colors: BrandColors): string | null {
  const solidFill = rPrEl.getElementsByTagNameNS(NS_A, "solidFill")[0];
  if (!solidFill) return null;
  const srgb = solidFill.getElementsByTagNameNS(NS_A, "srgbClr")[0];
  if (srgb) return srgb.getAttribute("val");
  const schemeClr = solidFill.getElementsByTagNameNS(NS_A, "schemeClr")[0];
  if (schemeClr) { const v = schemeClr.getAttribute("val"); return v ? (brandColorByScheme(colors, v) ?? null) : null; }
  return null;
}

function readXml(zip: PptxZip, path: string): Promise<Document | null> {
  const bytes = zip[path];
  if (!bytes) return Promise.resolve(null);
  const text = strFromU8(bytes);
  try {
    return Promise.resolve(new DOMParser().parseFromString(text, "application/xml"));
  } catch (error) {
    logBrandExtractError("brand-extract-read-xml", error);
    return Promise.resolve(null);
  }
}

function readRawString(zip: PptxZip, path: string): Promise<string | null> {
  const bytes = zip[path];
  if (!bytes) return Promise.resolve(null);
  try {
    return Promise.resolve(strFromU8(bytes));
  } catch (error) {
    logBrandExtractError("brand-extract-read-raw-string", error);
    return Promise.resolve(null);
  }
}

function isTitleOnlyName(name: string): boolean {
  if (!name) return false;
  return name.toLowerCase().replace(/[^a-z]/g, "").includes("titleonly");
}

function isWeakColor(hex: string | undefined, themeColors: BrandColors): boolean {
  if (!hex) return true;
  const norm = _norm(hex);
  if (!norm) return true;
  if (norm === "000000") return true;
  const dk1 = _norm(themeColors.dk1);
  return dk1 !== null && norm === dk1;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

function defaultColors(): BrandColors {
  return {
    dk1: "000000", dk2: "44546A", lt1: "FFFFFF", lt2: "E7E6E6",
    accent1: "4472C4", accent2: "ED7D31", accent3: "A5A5A5",
    accent4: "FFC000", accent5: "5B9BD5", accent6: "70AD47",
    hlink: "0563C1", folHlink: "954F72",
  };
}

function defaultFonts(): BrandFonts {
  return { titleFont: "Calibri Light", bodyFont: "Calibri", footnoteFont: "Calibri" };
}

function defaultTitleFormat(fonts: BrandFonts): PlaceholderFormat {
  return { fontName: fonts.titleFont, fontSize: 28, bold: true, fontColor: "000000", alignment: "left" };
}

function defaultBodyFormat(fonts: BrandFonts): PlaceholderFormat {
  return { fontName: fonts.bodyFont, fontSize: 14, bold: false, fontColor: "333333", alignment: "left" };
}

function defaultFootnoteFormat(fonts: BrandFonts): PlaceholderFormat {
  return { fontName: fonts.footnoteFont, fontSize: 8, bold: false, fontColor: "888888", alignment: "left" };
}

function defaultBulletLevels(fonts: BrandFonts): BulletLevel[] {
  return [
    { level: 0, fontSize: 14, fontName: fonts.bodyFont, fontColor: "333333", indentEmu: 228600, hangingEmu: -228600, bulletChar: "•" },
    { level: 1, fontSize: 12, fontName: fonts.bodyFont, fontColor: "333333", indentEmu: 457200, hangingEmu: -228600, bulletChar: "–" },
    { level: 2, fontSize: 11, fontName: fonts.bodyFont, fontColor: "505050", indentEmu: 685800, hangingEmu: -228600, bulletChar: "•" },
  ];
}

function defaultTableFormat(colors: BrandColors): TableFormat {
  return {
    headerRow: { fillColor: colors.accent1 ?? "4472C4", fontColor: "FFFFFF", fontName: "Calibri", fontSize: 10, bold: true },
    bodyRow: { fillColor: "FFFFFF", fontColor: "333333", fontName: "Calibri", fontSize: 10, bold: false },
    borderColor: "D2D0CE",
    borderWidth: 1,
  };
}

/**
 * Builds a full BrandSpec from just a colour scheme — for Saved Templates
 * entries with `themeColors` but no fetchable .pptx. Fonts/formats fall back
 * to the same defaults extractBrand() uses for a theme-less .pptx; only the
 * colours are real, the rest is editable in Review & Customise.
 */
export function buildBrandSpecFromThemeColors(rawColors: Partial<BrandColors>): BrandSpec {
  const colors: BrandColors = { ...defaultColors(), ...rawColors };
  const fonts = defaultFonts();
  const titleFormat = defaultTitleFormat(fonts);
  const bodyTextFormat = defaultBodyFormat(fonts);
  const footnoteFormat = defaultFootnoteFormat(fonts);
  // Mirrors resolveSubtitleFormat's own no-layout-found fallback above.
  const subtitleFormat: PlaceholderFormat = {
    fontName: fonts.bodyFont,
    fontSize: Math.max((bodyTextFormat.fontSize ?? 14) + 2, 18),
    bold: false,
    fontColor: colors.dk2 ?? colors.dk1 ?? "444444",
    alignment: "left",
  };
  const bulletLevels = defaultBulletLevels(fonts);
  const tableFormat = defaultTableFormat(colors);
  const chartFormat = buildChartFormat(colors);
  const fillColorMap = buildFillColorMap(colors);

  return {
    colors,
    fonts,
    titleFormat,
    subtitleFormat,
    bodyTextFormat,
    footnoteFormat,
    bulletLevels,
    tableFormat,
    tableDetected: false,
    chartFormat,
    fillColorMap,
    lineColor: colors.dk2 ?? colors.accent1 ?? "333333",
    highlightColor: colors.accent1 ?? colors.dk1 ?? "4472C4",
    bodyArea: null,
    titleOnlyLayout: null,
    templateSlideCount: 0,
  };
}

export { isDefaultFontColor } from "../../../utils/colourUtils";

// ─── Slide masters of a .pptx ──────────────────────────────────────────────
// Reads the slide masters (themes) of a .pptx — the active presentation or an
// uploaded file — for This Deck's Theme and the Upload tab (BRD FR-22, FR-24,
// FR-32). Each master reports its name, layout count, how many slides use it,
// and whether it is just the Office default theme (BR-07: no brand).

export interface MasterInfo {
  /** Stable id: the master's part path. */
  readonly id: string;
  readonly name: string;
  readonly masterPath: string;
  readonly themePath: string;
  readonly layoutCount: number;
  readonly slideCount: number;
  /** True for the stock "Office Theme" — it has no brand to apply. */
  readonly isOfficeDefault: boolean;
}

const OFFICE_DEFAULT = /^office([\s-]?(theme|design))?$/i;

/** Slide masters whose theme is just the Office default carry no brand (BR-07). */
export function isOfficeDefaultTheme(name: string): boolean {
  return OFFICE_DEFAULT.test(name.trim());
}

function relTargets(xml: string): string[] {
  return (xml.match(/<Relationship\b[^>]*\/>/g) ?? [])
    .map((tag) => /\bTarget="([^"]+)"/.exec(tag)?.[1])
    .filter((t): t is string => Boolean(t));
}

function baseName(path: string): string {
  return path.split("/").pop() ?? path;
}

function readText(zip: Record<string, Uint8Array>, path: string): string | null {
  const bytes = zip[path];
  return bytes ? strFromU8(bytes) : null;
}

function themeName(xml: string | null): string {
  if (!xml) return "";
  const match = /<a:theme\b[^>]*\bname="([^"]*)"/.exec(xml);
  return match?.[1] ? decodeXmlEntities(match[1]) : "";
}

function decodeXmlEntities(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

// Fully anchored (unlike a loose /(\d+)\.xml$/) so there's a single deterministic
// match attempt instead of one retried at every position in the path.
const MASTER_PATH_NUMBER = /^ppt\/slideMasters\/slideMaster(\d+)\.xml$/;
function masterPathNumber(path: string): number {
  return Number(MASTER_PATH_NUMBER.exec(path)?.[1]);
}

/** Parses the masters out of a .pptx buffer. Never throws: a package that
 *  can't be unzipped (corrupt, or password-protected) yields null. */
export function readMastersFromBuffer(data: ArrayBuffer): MasterInfo[] | null {
  let zip: Record<string, Uint8Array>;
  try {
    zip = unzipSync(new Uint8Array(data));
  } catch (error) {
    logBrandExtractError("brand-read-masters", error);
    return null;
  }

  const masterPaths = Object.keys(zip)
    .filter((p) => /^ppt\/slideMasters\/slideMaster\d+\.xml$/.test(p))
    .sort((a, b) => masterPathNumber(a) - masterPathNumber(b));

  // layout -> master, slide -> layout lookups
  const layoutToMaster = new Map<string, string>();
  for (const path of Object.keys(zip)) {
    const layoutMatch = /^ppt\/slideLayouts\/_rels\/(slideLayout\d+\.xml)\.rels$/.exec(path);
    if (!layoutMatch) continue;
    const master = relTargets(readText(zip, path) ?? "").find((t) => /slideMaster\d+\.xml$/.test(t));
    if (master) layoutToMaster.set(layoutMatch[1]!, `ppt/slideMasters/${baseName(master)}`);
  }
  const slidesPerMaster = new Map<string, number>();
  for (const path of Object.keys(zip)) {
    if (!/^ppt\/slides\/_rels\/slide\d+\.xml\.rels$/.test(path)) continue;
    const layout = relTargets(readText(zip, path) ?? "").find((t) => /slideLayout\d+\.xml$/.test(t));
    const master = layout ? layoutToMaster.get(baseName(layout)) : undefined;
    if (master) slidesPerMaster.set(master, (slidesPerMaster.get(master) ?? 0) + 1);
  }

  return masterPaths.map((masterPath) => {
    const rels = readText(zip, `ppt/slideMasters/_rels/${baseName(masterPath)}.rels`) ?? "";
    const themeTarget = relTargets(rels).find((t) => /theme\d+\.xml$/.test(t));
    const themePath = themeTarget ? `ppt/theme/${baseName(themeTarget)}` : "ppt/theme/theme1.xml";
    const name = themeName(readText(zip, themePath)) || "Slide master";
    const layoutCount = [...layoutToMaster.values()].filter((m) => m === masterPath).length;
    return {
      id: masterPath,
      name,
      masterPath,
      themePath,
      layoutCount,
      slideCount: slidesPerMaster.get(masterPath) ?? 0,
      isOfficeDefault: isOfficeDefaultTheme(name),
    };
  });
}

/** Masters that carry a brand — the Office master is never offered as a choice. */
export function brandMasters(masters: readonly MasterInfo[]): MasterInfo[] {
  return masters.filter((m) => !m.isOfficeDefault);
}
